import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authApi } from '../../services/api'
import { showSuccess, showError, showInfo } from './appSlice'

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials)
      
      if (response.success) {
        dispatch(showSuccess('Вход выполнен успешно'))
        return response.data.user
      } else {
        throw new Error(response.error?.message || 'Ошибка входа')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при входе в систему'))
      return rejectWithValue(error.message)
    }
  }
)

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch }) => {
    try {
      await authApi.logout()
      dispatch(showInfo('Вы вышли из системы'))
    } catch (error) {
      dispatch(showError('Ошибка при выходе из системы'))
    }
  }
)

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.me()
      
      if (response.success && response.data) {
        return response.data
      } else {
        throw new Error('Not authenticated')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (passwordData, { dispatch, rejectWithValue }) => {
    try {
      const response = await authApi.changePassword(passwordData)
      
      if (response.success) {
        dispatch(showSuccess('Пароль изменен успешно'))
        return response.data
      } else {
        throw new Error(response.error?.message || 'Ошибка смены пароля')
      }
    } catch (error) {
      dispatch(showError(error.message || 'Ошибка при смене пароля'))
      return rejectWithValue(error.message)
    }
  }
)

export const refreshSession = createAsyncThunk(
  'auth/refreshSession',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.refresh()
      
      if (response.success && response.data?.user) {
        return response.data.user
      } else {
        throw new Error('Session refresh failed')
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    
    updateUser: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false
        state.user = null
        state.isAuthenticated = false
        state.error = action.payload
      })
    
    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false
        state.user = null
        state.isAuthenticated = false
        state.error = null
      })
      .addCase(logout.rejected, (state) => {
        state.isLoading = false
        state.user = null
        state.isAuthenticated = false
        state.error = null
      })
    
    // Check Auth
    builder
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.user = action.payload
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(checkAuth.rejected, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.error = null
      })
    
    // Change Password
    builder
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    
    // Refresh Session
    builder
      .addCase(refreshSession.fulfilled, (state, action) => {
        state.user = action.payload
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(refreshSession.rejected, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.error = null
      })
  },
})

export const { clearError, updateUser } = authSlice.actions

// Selectors
export const selectIsAdmin = (state) => state.auth.user?.role === 'admin'
export const selectIsEditor = (state) => state.auth.user?.role === 'editor'
export const selectCanManage = (state) => {
  const role = state.auth.user?.role
  return role === 'admin' || role === 'editor'
}

export default authSlice.reducer